import { IBrowserAccessor } from './browser';
import { ILogger } from './logger';
import { expect } from './expect';
import {
  BFGCLID,
  BorderfreeGoogleAdsHandlerSettings,
  BorderfreeGoogleAdsPreset,
  BorderfreeGoogleAds, STORAGE_KEY_BORDERFREE_GOOGLE_ADS_PARAMETERS,
} from './borderfree-google-ads.types';

export class BorderfreeGoogleAdsHandler {
  private readonly browser: IBrowserAccessor;
  private readonly logger: ILogger;
  private readonly bfIsEnabled: boolean;
  private readonly bfLifetimeInDays: number;

  private bfgclid: string;

  /*
   * Gets parsed Google Click ID provided by Borderfree.
   */
  public get googleClickId(): string {
    return this.bfgclid;
  }

  /*
   * Gets true when Borderfree logic is enabled and Borderfree gclid parameter found.
   */
  public get isBorderfreeGoogleClickId(): string {
    return this.bfIsEnabled && this.bfgclid;
  }

  /**
   * Initializes a new instance of GoogleAdsHandler class.
   */
  constructor(browser: IBrowserAccessor, logger: ILogger, settings?: BorderfreeGoogleAdsHandlerSettings) {
    this.browser = expect(browser)
      .toBeDefined('The IBrowserAccessor implementation is required to construct GoogleAdsHandler.');
    this.logger = expect(logger)
      .toBeDefined('The ILogger implementation is required to construct GoogleAdsHandler.');

    this.bfIsEnabled = settings?.bfGoogleAdsEnabled ?? true;
    this.bfLifetimeInDays = settings?.bfGoogleAdsLifetimeInDays ?? 30;
  }

  /**
   * Parses Google Click ID (gclid) parameter from the URL.
   */
  public async handle(customInput?: BorderfreeGoogleAdsPreset): Promise<void> {
    if (!this.bfIsEnabled) {
      return;
    }

    const input = this.parseUrl(customInput);
    const storageStrategy = new BorderfreeStorageStrategy(this.browser, this.bfLifetimeInDays);
    const output = await storageStrategy.execute(input);
    this.bfgclid = output.bfgclid;
  }

  private parseUrl(input?: string | BorderfreeGoogleAdsPreset): BorderfreeGoogleAds {
    const result = { bfgclid: null };
    try {
      if (isUrl(input)) { // if URL with gclid provided outside, then parse it assuming it contains gclid
        return getFromPresetUrl(input)
      }
      if (isPresetObject(input)) { // if object with gclid provided then use it as output
        return getFromPresetObject(input);
      }

      // default flow is to parse URL or get defaults based on referrer
      const url = new URL(this.browser.getPageUrl());
      result.bfgclid = url.searchParams.get(BFGCLID);
    } catch (error) {
      this.logger.writeErrorLog({
        method: 'GoogleAdsHandler::parseUrl()',
        message: error.message,
        errorMessage: error.message,
        errorStackTrace: error.stackTrace,
      });
    }

    return result;

    function isUrl(_input: unknown): _input is string {
      return typeof _input === "string";
    }
    function getFromPresetUrl(_input: string): BorderfreeGoogleAds {
      const url = new URLSearchParams(_input);
      return {
        bfgclid: url.get(BFGCLID)
      };
    }
    function isPresetObject(_input: unknown): _input is BorderfreeGoogleAds {
      return (
        typeof _input === "object" &&
        _input !== null &&
        "bfgclid" in _input &&
        typeof (_input as BorderfreeGoogleAds).bfgclid === "string"
      );
    }
    function getFromPresetObject(_input: BorderfreeGoogleAds): BorderfreeGoogleAds {
      return _input;
    }
  }
}

interface IStorageStrategy {
  execute(googleAdsParameters: BorderfreeGoogleAds): Promise<BorderfreeGoogleAds>;
}

class BorderfreeStorageStrategy implements IStorageStrategy {
  private readonly browser: IBrowserAccessor;
  private readonly bfLifetimeInDays: number;
  constructor(browser: IBrowserAccessor, bfLifetimeInDays: number) {
    this.browser = expect(browser)
      .toBeDefined('The IBrowserAccessor implementation is required to construct BorderfreeStorageStrategy.');
    this.bfLifetimeInDays = bfLifetimeInDays;
  }

  public async execute(parameters: BorderfreeGoogleAds): Promise<BorderfreeGoogleAds> {
    try {
      if (parameters.bfgclid) {
        const outsideParameters = {
          bfgclid: parameters.bfgclid,
          expiry: Date.now() + this.bfLifetimeInDays * 24 * 60 * 60 * 1000 // 30 days
        };
        const outsideParametersJson = JSON.stringify(outsideParameters);
        await this.browser.localStorage.setItem(STORAGE_KEY_BORDERFREE_GOOGLE_ADS_PARAMETERS, outsideParametersJson);
        return { bfgclid: parameters.bfgclid };
      } else {
        const existingParametersJson = await this.browser.localStorage.getItem(STORAGE_KEY_BORDERFREE_GOOGLE_ADS_PARAMETERS);
        const existingParameters = JSON.parse(existingParametersJson);
        if (existingParameters?.expiry && existingParameters.expiry < Date.now()) {
          await this.browser.localStorage.removeItem(STORAGE_KEY_BORDERFREE_GOOGLE_ADS_PARAMETERS);
        } else if (existingParameters?.bfgclid) {
          return { bfgclid: existingParameters.bfgclid };
        } else if (existingParameters?.gclid) {
          return { bfgclid: null };
        }

        return { bfgclid: null };
      }
    } catch {
      return { bfgclid: null };
    }
  }
}
